// ... existing code ...
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Autocomplete,
  createFilterOptions,
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  Divider,
  CircularProgress,
  Avatar,
  MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { practiceSettingsControls } from '../../auth/pageControls';
import { WORKSTATION_LOCK_QUERY_KEY, useWorkstationLockMinutes } from '../../auth/useIdleLock';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { UploadService } from '../../services/uploadService';
import { withOfflineFallback, readClinic, OfflineNotice, useIsOffline } from '../../offline';
import { allTimezoneOptions, TimezoneOption } from '../../utils/timezones';

// The Autocomplete's default filter searches `getOptionLabel` only, so a vet
// who types the IANA id they know — "America/New_York" — would get "No options"
// behind the hand-written label "Eastern — New York". Search the id too.
const filterTimezones = createFilterOptions<TimezoneOption>({
  stringify: (option) => `${option.label} ${option.value}`,
});

const PracticeSettings: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const rbac = useRBAC();
  const { claims, loading: claimsLoading } = rbac;
  const can = practiceSettingsControls(rbac);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    timezone: '',
  });

  // Every write here (the form, the logo, the lock screen) is OWNER/ADMIN + manage:practice_settings.
  const isEditable = can.edit;
  const lockMinutes = useWorkstationLockMinutes();

  const uploadLogoMutation = useMutation(
    (file: File) => UploadService.uploadClinicLogo(file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clinic']);
        enqueueSnackbar('Logo uploaded successfully', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error.response?.data?.message || 'Failed to upload logo',
          { variant: 'error' },
        );
      },
    },
  );

  const removeLogoMutation = useMutation(
    () => UploadService.removeClinicLogo(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clinic']);
        enqueueSnackbar('Logo removed successfully', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error.response?.data?.message || 'Failed to remove logo',
          { variant: 'error' },
        );
      },
    },
  );

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar('File too large. Maximum size is 2MB', { variant: 'error' });
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      enqueueSnackbar('Invalid file type. Allowed: PNG, JPEG, WebP', { variant: 'error' });
      return;
    }

    uploadLogoMutation.mutate(file);
    // Reset the input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const { data: clinicData, isLoading: clinicLoading } = useQuery(
    ['clinic', claims?.clinicId],
    withOfflineFallback(
      async () => {
        if (!claims?.clinicId) return null;
        const response = await api.get(`/clinics/${claims.clinicId}`);
        return response.data;
      },
      readClinic,
    ),
    {
      enabled: !!claims?.clinicId,
    }
  );

  // Hydrate from the query DATA, not onSuccess: react-query v3 fires
  // onSuccess only on real fetches, so a remount served from still-fresh
  // cache (staleTime) rendered an all-empty form until a hard refresh.
  // Structural sharing keeps the data identity stable across no-op refetches,
  // so this doesn't clobber in-progress edits unless the server row changed.
  useEffect(() => {
    if (!clinicData) return; // replica miss — keep the form as-is
    setFormData({
      name: clinicData.name || '',
      phone: clinicData.phone || '',
      email: clinicData.email || '',
      address: clinicData.address || '',
      // GET /clinics/:id is un-narrowed, so the row's timezone has always been
      // on the wire — this screen simply never read it (DATE-KINDS.md §3.4).
      timezone: clinicData.timezone || '',
    });
  }, [clinicData]);

  const updateMutation = useMutation(
    (data: Record<string, any>) => api.put(`/clinics/${claims?.clinicId}`, data),
    {
      onSuccess: () => {
        // The prefix covers ['clinic','current'], which every screen formats times with.
        queryClient.invalidateQueries(['clinic']);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message || 'Failed to update practice settings',
          { variant: 'error' },
        );
      },
    }
  );

  // Lock screen (Beta), workstation-lock plan T6: saved the moment it is chosen, like a switch.
  const lockMutation = useMutation(
    async (idleLockMinutes: number | null) =>
      (await api.put('/clinics/workstation-lock', { idleLockMinutes })).data as {
        idleLockMinutes: number | null;
      },
    {
      onSuccess: (saved) => {
        queryClient.setQueryData(WORKSTATION_LOCK_QUERY_KEY, saved);
        enqueueSnackbar(
          saved?.idleLockMinutes
            ? `The screen now locks after ${saved.idleLockMinutes} idle minutes.`
            : 'The lock screen is off.',
          { variant: 'success' },
        );
      },
      onError: () => {
        enqueueSnackbar('Could not save the lock screen setting.', { variant: 'error' });
      },
    },
  );

  const timezoneOptions: TimezoneOption[] = useMemo(() => {
    const base = allTimezoneOptions();
    // A clinic already sitting on a zone outside the shortlist must still see
    // its own value — an Autocomplete whose `value` is absent from `options`
    // renders blank and would look like the setting had been wiped. This is not
    // only the exotic-zone case: where the browser lacks
    // `Intl.supportedValuesOf`, `allTimezoneOptions()` IS the curated shortlist.
    const withCurrent =
      formData.timezone && !base.some((o) => o.value === formData.timezone)
        ? [
            ...base,
            {
              value: formData.timezone,
              label: formData.timezone.replace(/_/g, ' '),
              common: false,
            },
          ]
        : base;

    // `groupBy` renders a fresh header every time the group CHANGES down the
    // list, so the two groups have to be contiguous: curated first in their
    // hand-ordered sequence (Eastern before Pacific, not alphabetical), then
    // everything else A-Z — including a current value appended just above.
    const common = withCurrent.filter((o) => o.common);
    const rest = withCurrent
      .filter((o) => !o.common)
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...common, ...rest];
  }, [formData.timezone]);

  const selectedTimezone =
    timezoneOptions.find((o) => o.value === formData.timezone) ?? null;

  const handleSave = () => {
    if (!isEditable) return;
    updateMutation.mutate({
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      // Omitted rather than sent empty: the column is NOT NULL with a schema
      // default, and the backend DTO rejects '' — an unset picker must not 400
      // an unrelated name/phone save.
      ...(formData.timezone ? { timezone: formData.timezone } : {}),
    });
  };

  if (authLoading || claimsLoading || clinicLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  const readOnlyProps = isEditable
    ? {}
    : {
        InputProps: { readOnly: true },
      };

  const renderAlert = !isEditable ? (
    <Alert severity="info" sx={{ mb: 3 }}>
      Only admins and owners can edit practice settings.
    </Alert>
  ) : null;

  return (
    <Box>
      {renderAlert}
      {/* Clinic-profile edits are rare admin ops — blocked offline, never queued. */}
      <OfflineNotice sx={{ mb: 3 }}>
        Practice settings need an internet connection to save changes.
      </OfflineNotice>
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your practice information has been successfully updated.
        </Alert>
      )}

      {/* Practice Info + Logo */}
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Box sx={{ flex: '1 1 0', maxWidth: 650 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Practice Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            General details about your veterinary practice.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Practice Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              size="small"
              fullWidth
              {...readOnlyProps}
            />
            <TextField
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              size="small"
              fullWidth
              {...readOnlyProps}
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              size="small"
              fullWidth
              sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
              {...readOnlyProps}
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              multiline
              rows={3}
              size="small"
              fullWidth
              sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
              {...readOnlyProps}
            />
            {/*
              Time zone is NOT a display preference — it decides when reminder
              sends fire, what period a statement covers and which day a
              hospital charge lands on. This picker is the column's first writer
              anywhere in the product; before it, every clinic was stuck on the
              America/New_York schema default.
            */}
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <Autocomplete
                data-testid="clinic-timezone"
                options={timezoneOptions}
                value={selectedTimezone}
                onChange={(_e, option) =>
                  setFormData({ ...formData, timezone: option?.value ?? '' })
                }
                getOptionLabel={(option) => option.label}
                groupBy={(option) => (option.common ? 'Common' : 'All time zones')}
                filterOptions={filterTimezones}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                readOnly={!isEditable}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Time zone"
                    helperText="Used for reminder send times, statement cut-offs and billing dates."
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', flexShrink: 0, width: 160, alignSelf: { xs: 'center', sm: 'flex-start' }, pt: { xs: 0, sm: 3.5 } }}>
          {clinicData?.logoUrl ? (
            <Avatar
              src={clinicData.logoUrl}
              alt={clinicData.name || 'Clinic logo'}
              sx={{ width: 110, height: 110, mx: 'auto', mb: 1.5, border: '2px solid', borderColor: 'divider' }}
            />
          ) : (
            <Avatar
              sx={{ width: 110, height: 110, mx: 'auto', mb: 1.5, bgcolor: 'grey.100', border: '2px dashed', borderColor: 'grey.300' }}
            >
              <BusinessIcon sx={{ fontSize: 44, color: 'grey.400' }} />
            </Avatar>
          )}
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={
              uploadLogoMutation.isLoading ? (
                <CircularProgress size={14} />
              ) : (
                <CloudUploadIcon fontSize="small" />
              )
            }
            component="label"
            disabled={!isEditable || uploadLogoMutation.isLoading || isOffline}
            sx={{ mb: 0.5, fontSize: '0.75rem' }}
          >
            {clinicData?.logoUrl ? 'Change' : 'Upload'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLogoSelect}
            />
          </Button>
          {clinicData?.logoUrl && (
            <Button
              variant="text"
              size="small"
              fullWidth
              color="error"
              startIcon={
                removeLogoMutation.isLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )
              }
              onClick={() => removeLogoMutation.mutate()}
              disabled={!isEditable || removeLogoMutation.isLoading || isOffline}
              sx={{ fontSize: '0.75rem' }}
            >
              Remove
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
            PNG, JPG, or WebP. Max 2MB.
          </Typography>
          {isOffline && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
              Logo changes need an internet connection.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isEditable || isOffline}
        >
          Save Changes
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />
      <Typography variant="subtitle1" fontWeight={600}>
        Lock screen (Beta)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 650 }}>
        For shared computers: After the selected amount of idle time, the screen will automatically
        lock. Any team member can then unlock it using their own name and PIN.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 650 }}>
        Each team member can create their personal PIN by selecting Set My PIN from the menu under
        their initials.
      </Typography>
      <TextField
        id="workstation-lock-minutes"
        select
        label="Lock after"
        value={lockMinutes === undefined ? '' : lockMinutes === null ? 'off' : String(lockMinutes)}
        onChange={(e) => lockMutation.mutate(e.target.value === 'off' ? null : Number(e.target.value))}
        disabled={!isEditable || isOffline || lockMinutes === undefined || lockMutation.isLoading}
        size="small"
        sx={{ minWidth: 220 }}
      >
        <MenuItem value="off">Off</MenuItem>
        {[5, 10, 15].map((minutes) => (
          <MenuItem key={minutes} value={String(minutes)}>
            {minutes} minutes
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default PracticeSettings; 